import {
  Alert,
  Badge,
  Box,
  Container,
  Header,
  SpaceBetween
} from "@cloudscape-design/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingColumns } from "@fortawesome/free-solid-svg-icons";

import { OrderSummaryProps } from "types/invoiceTypes";
import { getCurrencySymbol } from "utils/Utils";
import { securityAddOnsList } from "configs/settings";
import { freeTrialPeriodDays } from "configs/settings";

const OrderSummaryCard = ({
  orderSummary,
  currency,
  securityAddOns,
  hasUsedFreeTrial,
  deploymentData
}: OrderSummaryProps) => {
  
  const currencySymbol = getCurrencySymbol(currency);

  const isYearlySelected = deploymentData.billingCycle === "yearly";

  const selectedSecurityAddOns = securityAddOnsList.filter((addOn) =>
  securityAddOns.includes(addOn.id)
);

  return (
    <Container 
        header={
          <Header variant="h3">
            Order Summary {isYearlySelected ? <Badge color="green">Save 20%</Badge> : undefined}
          </Header>
        }
      >
      <br />

      <SpaceBetween size="xxl">

        {/* Plan & Security */}
        <SpaceBetween size="l">
          <div className="flex justify-space-between wrap row-gap-5 col-gap-10">
            <SpaceBetween size="xxxs">
              <Box variant="h4">
                {orderSummary.planTitle}
              </Box>

              <div>
                <Box >
                {orderSummary.planSize} size
                </Box>

                {/* <br />

                <Badge color="blue">
                  {orderSummary.planBadgeText}
                </Badge> */}
              </div>
            </SpaceBetween>

            {isYearlySelected ? (
              <SpaceBetween size="xxxs" alignItems="end">
                <Box color="text-status-inactive">
                  <del>
                    {currencySymbol}
                    {orderSummary.yearlyOriginalTotal}
                  </del>
                </Box>

                <Box variant="h4">
                  {currencySymbol}
                  {orderSummary.total}
                </Box>
              </SpaceBetween>
            ) : (
              <Box variant="h4">
                {currencySymbol}
                {orderSummary.total}
              </Box>
            )}
          </div>

          {/* Security */}
          {selectedSecurityAddOns.length > 0 && (
            <div className="flex justify-space-between wrap row-gap-5 col-gap-10">
              <SpaceBetween size="xxxs">
                <Box variant="h4">Security Add-Ons</Box>

                {selectedSecurityAddOns.map((addOn) => (
                  <Box variant="small" key={addOn.id}>
                    {addOn.name}
                  </Box>
                ))}
              </SpaceBetween>
              

              {isYearlySelected ? (
                <SpaceBetween size="xxxs" alignItems="end">
                  <Box color="text-status-inactive">
                    <del>
                      {currencySymbol}
                      {orderSummary.yearlyOriginalSecurityPrice}
                    </del>
                  </Box>

                  <Box variant="h4">
                    {currencySymbol}
                    {orderSummary.securityPrice}
                  </Box>
                </SpaceBetween>
              ) : (
                <Box variant="h4">
                  {currencySymbol}
                  {orderSummary.securityPrice}
                </Box>
              )}
            </div>
          )}
        </SpaceBetween>

        {/* Price breakdown */}
        <SpaceBetween size="l">
          <SpaceBetween size="s">

            <div className="flex justify-space-between wrap">
              <Box>Subtotal</Box>

              {isYearlySelected ? (
                <SpaceBetween size="xxxs" alignItems="end">
                  <Box color="text-status-inactive">
                    <del>
                      {currencySymbol}
                      {orderSummary.yearlyOriginalSubtotal}
                    </del>
                  </Box>

                  <Box>
                    <strong>
                      {currencySymbol}
                      {orderSummary.subtotal}
                    </strong>
                  </Box>
                </SpaceBetween>
              ) : (
                <Box>
                  <strong>
                    {currencySymbol}
                    {orderSummary.subtotal}
                  </strong>
                </Box>
              )}
            </div>

            <div className="space-between">
              <Box>Tax</Box>

              <Box>
                <strong>
                  {currencySymbol}
                  {orderSummary.tax}
                </strong>
              </Box>
            </div>

          </SpaceBetween>

          {/* Trial info */}
          {!hasUsedFreeTrial && (
            <Alert
              type="success"
              header= {`${freeTrialPeriodDays}-Day Free Trial`}
            >
              Full feature access. No charge until trial ends.
            </Alert>
          )}
        </SpaceBetween>

        {/* Total today */}
        <SpaceBetween size="s">
          <div className="flex justify-space-between wrap">
            <Box variant="h4">
              Total Today
            </Box>

            {!hasUsedFreeTrial ? (
              <h1 className="main-field m-b-0 m-t-0">
                {currencySymbol}0.00
              </h1>
            ) : isYearlySelected ? (
              <SpaceBetween size="xxxs" alignItems="end">
                <Box variant="h1" color="text-status-inactive">
                  <del>
                    {currencySymbol}
                    {orderSummary.yearlyOriginalTotalToday}
                  </del>
                </Box>

                <h1 className="main-field m-b-0 m-t-0">
                  {currencySymbol}
                  {orderSummary.totalToday}
                </h1>
              </SpaceBetween>
            ) : (
              <h1 className="main-field m-b-0 m-t-0">
                {currencySymbol}
                {orderSummary.totalToday}
              </h1>
            )}
          </div>

          {!hasUsedFreeTrial && (
            <div className="reverse-flex-row">
              <Box variant="small">
                First billing of {currencySymbol}
                {orderSummary.totalToday} on {orderSummary.dueDateFormated}
              </Box>
            </div>
          )}
        </SpaceBetween>

        {/* Payment method */}
        <SpaceBetween size="s">
          <Box variant="h5">
            PAYMENT METHOD
          </Box>

          <Container>
            <div className="flex-row gap-10">
              <FontAwesomeIcon
                icon={faBuildingColumns}
                className="font-size-18"
              />

              <div>Bank Transfer</div>
            </div>
          </Container>
        </SpaceBetween>

      </SpaceBetween>
    </Container>
  );
};

export default OrderSummaryCard;